/**
 * TanStack Query hooks for the Creators section.
 * Wraps GET /v1/admin/creators[/:id].
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import { getToken, logout } from '@/auth/admin-auth';

import type {
  CreatorDetail,
  CreatorInvite,
  CreatorStatsResponse,
  CreatorsListResponse,
  OutreachChannel,
  PipelineStage,
} from './types';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export interface CreatorInviteTemplate {
  id: string;
  page_headline: string | null;
  page_subtitle: string | null;
  page_cta: string | null;
  page_bullet_titles: string[] | null;
  page_bullets: string[] | null;
  page_image_url: string | null;
}

export interface CreatorsListParams {
  q?: string;
  status?: PipelineStage;
  limit?: number;
  offset?: number;
}

function buildListQuery(params: CreatorsListParams): string {
  const usp = new URLSearchParams();
  if (params.q) usp.set('q', params.q);
  if (params.status) usp.set('status', params.status);
  if (params.limit !== undefined) usp.set('limit', String(params.limit));
  if (params.offset !== undefined) usp.set('offset', String(params.offset));
  const qs = usp.toString();
  return qs ? `/v1/admin/creators?${qs}` : '/v1/admin/creators';
}

export function useCreateCreatorInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email?: string;
      desired_slug?: string;
      display_name?: string;
      platform?: string;
      handle?: string;
      social_url?: string;
      pitch?: string;
      page_headline?: string;
      page_subtitle?: string;
      page_cta?: string;
      page_bullet_titles?: string[];
      page_bullets?: string[];
      page_image_url?: string;
      outreach_stage?: CreatorInvite['outreach_stage'];
      last_contacted_at?: string | null;
      next_follow_up_at?: string | null;
      outreach_note?: string | null;
    }) =>
      api.post<{ invite: CreatorInvite; claim_url: string }>('/v1/admin/creators/invites', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useCreatorInviteTemplate() {
  return useQuery({
    queryKey: ['admin', 'creators', 'invite-template'],
    queryFn: () =>
      api.get<{ template: CreatorInviteTemplate }>('/v1/admin/creators/invite-template'),
  });
}

export function useUpdateCreatorInviteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      page_headline?: string | null;
      page_subtitle?: string | null;
      page_cta?: string | null;
      page_bullet_titles?: string[] | null;
      page_bullets?: string[] | null;
      page_image_url?: string | null;
    }) =>
      api.patch<{ template: CreatorInviteTemplate }>('/v1/admin/creators/invite-template', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators', 'invite-template'] }),
  });
}

export function useCreatorInviteDetail(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'creators', 'invite', id],
    queryFn: () => api.get<{ invite: CreatorInvite }>(`/v1/admin/creators/invites/${id}`),
    enabled: Boolean(id),
  });
}

export function useUpdateCreatorInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<
        Pick<
          CreatorInvite,
          | 'email'
          | 'desired_slug'
          | 'display_name'
          | 'platform'
          | 'handle'
          | 'social_url'
          | 'pitch'
          | 'page_headline'
          | 'page_subtitle'
          | 'page_cta'
          | 'page_bullet_titles'
          | 'page_bullets'
          | 'page_image_url'
          | 'outreach_stage'
          | 'last_contacted_at'
          | 'next_follow_up_at'
          | 'outreach_note'
        >
      >;
    }) => api.patch<{ invite: CreatorInvite }>(`/v1/admin/creators/invites/${id}`, body),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'creators'] });
      qc.invalidateQueries({ queryKey: ['admin', 'creators', 'invite', variables.id] });
    },
  });
}

/**
 * Mark an invite as sent on a specific channel. Server flips outreach_stage to
 * `'invited'`, stamps `last_contacted_at`, and persists the channel into the
 * new `outreach_channel` column. Backwards-compatible: if `channel` is omitted
 * the backend defaults to `'other'`.
 */
export function useMarkInviteSent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, channel }: { id: string; channel?: OutreachChannel }) =>
      api.post<{ invite: CreatorInvite }>(
        `/v1/admin/creators/invites/${id}/mark-contacted`,
        channel ? { channel } : undefined,
      ),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['admin', 'creators'] });
      qc.invalidateQueries({ queryKey: ['admin', 'creators', 'invite', variables.id] });
    },
  });
}

/**
 * @deprecated Prefer `useMarkInviteSent`. Kept as a shim during the W2 rollout
 * so any non-creators caller still resolves; calls the new hook with no
 * channel (server defaults to `'other'`).
 */
export const useMarkCreatorInviteContacted = useMarkInviteSent;

/**
 * Per-creator stats card. Backed by `GET /v1/admin/creators/:id/stats` —
 * returns all-time and last-7d counts for views / leads / accounts / entries
 * sourced from `CreatorAttributionEvent` server-side.
 */
export function useCreatorStats(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'creators', 'stats', userId],
    queryFn: () => api.get<CreatorStatsResponse>(`/v1/admin/creators/${userId}/stats`),
    enabled: Boolean(userId),
  });
}

export interface ExportInvitesCsvParams {
  /** Filter by invite stage. Defaults to `'draft'` for the lead bulk export. */
  status?: 'draft' | 'invited' | 'claimed' | 'expired';
  /** Cap on rows. Server caps at 500. */
  limit?: number;
}

/**
 * Bulk-download draft invites as CSV. Triggers a browser-side blob download.
 * Returns a mutation rather than a query because the action is button-driven
 * and the file is not cached.
 */
export function useExportInvitesCsv() {
  return useMutation({
    mutationFn: async (params: ExportInvitesCsvParams = {}) => {
      const usp = new URLSearchParams();
      usp.set('status', params.status ?? 'draft');
      if (params.limit !== undefined) usp.set('limit', String(params.limit));
      const path = `/v1/admin/creators/invites/export.csv?${usp.toString()}`;
      const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

      const headers: Record<string, string> = { Accept: 'text/csv' };
      const token = getToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(url, { method: 'GET', headers });

      if (res.status === 401) {
        logout();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
        throw new ApiError(401, 'Unauthorized', null);
      }
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new ApiError(res.status, `CSV export failed (${res.status})`, text);
      }

      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') ?? '';
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const filename =
        match?.[1] ??
        `creator-invites-${params.status ?? 'draft'}-${new Date().toISOString().slice(0, 10)}.csv`;

      // Trigger download.
      if (typeof window !== 'undefined') {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Free the object URL after the click registers.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
      }

      return { filename, size: blob.size };
    },
  });
}

export function useReviewCreator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      status,
      review_note,
    }: {
      userId: string;
      status: 'approved' | 'rejected' | 'pending';
      // Optional admin note. Especially relevant for `rejected` so the
      // creator sees the reason in their Studio rejected banner; backend
      // already accepts the field on `reviewBody`.
      review_note?: string;
    }) =>
      api.patch(`/v1/admin/creators/${userId}/status`, {
        status,
        ...(review_note ? { review_note } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useUpdateCreatorProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      body,
    }: {
      userId: string;
      body: {
        display_name?: string;
        public_slug?: string;
        bio?: string | null;
        avatar_url?: string | null;
        banner_url?: string | null;
        website_url?: string | null;
        accent_color?: string | null;
        share_headline?: string | null;
        share_body?: string | null;
        world_cup_country?: string | null;
        world_cup_winner_pick?: string | null;
        world_cup_hope_pick?: string | null;
      };
    }) => api.patch(`/v1/admin/creators/${userId}/profile`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useReviewCreatorSocial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'APPROVED' | 'REJECTED' | 'PENDING' }) =>
      api.patch(`/v1/admin/creators/socials/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useReviewCreatorPrize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'APPROVED' | 'REJECTED' | 'PENDING' }) =>
      api.patch(`/v1/admin/creators/prizes/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useReviewCreatorCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: 'approved' | 'rejected' | 'pending' | 'draft';
    }) =>
      api.patch(`/v1/admin/creators/competitions/${id}/status`, { creator_publish_status: status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useUpdateCreatorCompetition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        creator_cover_url?: string | null;
        creator_join_opens_at?: string | null;
        entry_deadline?: string | null;
        creator_share_headline?: string | null;
        creator_share_body?: string | null;
      };
    }) => api.patch(`/v1/admin/creators/competitions/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'creators'] }),
  });
}

export function useCreatorsList(params: CreatorsListParams) {
  return useQuery({
    queryKey: ['admin', 'creators', 'list', params],
    queryFn: () => api.get<CreatorsListResponse>(buildListQuery(params)),
  });
}

export function useCreatorDetail(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'creators', 'detail', id],
    queryFn: () => api.get<CreatorDetail>(`/v1/admin/creators/${id}`),
    enabled: Boolean(id),
  });
}
